import React, { useState } from 'react';
import { useSceneUpdate } from '../../hooks/useSceneUpdate';
import './StaticSceneInfo.css';
import CustomSelect from '../CustomSelect';

const StaticSceneInfo = ({
  novelId,
  sceneId,
  scene,
  setScene,
  novel,
  loading,
  setLoading,
  error,
  setError,
  isProduction = false
}) => {
  const { saveScene } = useSceneUpdate(novelId, sceneId, isProduction, setScene, setLoading, 'static');
  const [editingInfo, setEditingInfo] = useState(false);

  const getFormData = () => ({
    name: scene?.name || '',
    tag: scene?.tag || '',
    description: scene?.description || '',
    original_message: scene?.original_message || '',
    background_id: scene?.background_id || '',
    characters: scene?.characters || [],
    music_id: scene?.music_id || '',
    speaker_id: scene?.speaker_id || (novel?.characters?.length > 0 ? novel.characters[0].id : ''),
    speaker_type: scene?.speaker_type || 'character'
  });

  const [infoFormData, setInfoFormData] = useState(getFormData);

  // Helper functions
  const getBackgroundName = (backgroundId) => {
    if (!backgroundId) return 'Выберите фон';
    const background = novel?.assets?.backgrounds?.find(bg => bg.id === backgroundId);
    return background?.name || 'Неизвестный фон';
  };

  const getCharacterName = (characterId) => {
    if (!characterId) return 'Неизвестный персонаж';
    const character = novel?.characters?.find(char => char.id === characterId);
    if (!character) return 'Неизвестный персонаж';

    // Try to get name from translations first
    const nameTranslation = character.name_translations?.find(t => t.language === 'russian');
    if (nameTranslation?.translation) {
      return nameTranslation.translation;
    }

    // Fallback to prompt_name or default
    return character.prompt_name || 'Неизвестный персонаж';
  };

  const getSpriteMixName = (characterId, spriteMixId) => {
    if (!spriteMixId) return 'Без набора спрайтов';
    const character = novel?.characters?.find(char => char.id === characterId);
    const spriteMix = character?.sprites?.find(mix => mix.id === spriteMixId);
    return spriteMix?.name || 'Неизвестный микс';
  };

  const getSpriteName = (spriteId) => {
    if (!spriteId) return 'Без спрайта';
    const sprite = novel?.assets?.sprites?.find(spr => spr.id === spriteId);
    return sprite?.name || 'Неизвестный спрайт';
  };

  // Helper function to get selected character for filtering
  const getSelectedCharacter = (characterId) => {
    return novel?.characters?.find(char => char.id === characterId);
  };

  // Helper function to get selected sprite mix for filtering
  const getSelectedSpriteMix = (characterId, spriteMixId) => {
    const character = getSelectedCharacter(characterId);
    if (!character?.sprites || !spriteMixId) return null;
    return character.sprites.find(sprite => sprite.id === spriteMixId);
  };

  const getMusicName = (musicId) => {
    if (!musicId) return 'Без музыки';
    const music = novel?.assets?.musics?.find(mus => mus.id === musicId);
    return music?.name || 'Неизвестная музыка';
  };

  const addCharacter = () => {
    const newCharacter = {
      character_id: '',
      sprite_mix_id: '',
      sprite_id: ''
    };
    setInfoFormData({
      ...infoFormData,
      characters: [...infoFormData.characters, newCharacter]
    });
  };

  const updateCharacter = (index, field, value) => {
    const updatedCharacters = [...infoFormData.characters];
    updatedCharacters[index] = {
      ...updatedCharacters[index],
      [field]: value
    };
    setInfoFormData({
      ...infoFormData,
      characters: updatedCharacters
    });
  };

  const removeCharacter = (index) => {
    const updatedCharacters = infoFormData.characters.filter((_, i) => i !== index);
    setInfoFormData({
      ...infoFormData,
      characters: updatedCharacters
    });
  };

  const handleEditInfo = () => {
    setInfoFormData(getFormData());
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    if (loading) return;
    setError(null);
    const updatedScene = {
      ...scene,
      name: infoFormData.name,
      tag: infoFormData.tag,
      description: infoFormData.description,
      original_message: infoFormData.original_message,
      background_id: infoFormData.background_id,
      characters: infoFormData.characters,
      music_id: infoFormData.music_id || null,
      speaker_id: infoFormData.speaker_id || null,
      speaker_type: infoFormData.speaker_type
    };
    const success = await saveScene(updatedScene, 'Информация сцены успешно сохранена!');
    if (success) setEditingInfo(false);
  };

  const handleCancelInfo = () => {
    setInfoFormData(getFormData());
    setEditingInfo(false);
  };

  return (
    <div className="ssi-wrap">
      <div className="ssi-header">
        <h3>Информация о сцене</h3>
        {!editingInfo && (
          <button
            className="ssi-btn-edit"
            onClick={handleEditInfo}
            disabled={loading}
          >
            Редактировать
          </button>
        )}
      </div>

      {editingInfo ? (
        <div className="ssi-edit-form">
          <div className="ssi-form-group">
            <label>Название сцены:</label>
            <input
              type="text"
              value={infoFormData.name}
              onChange={(e) => setInfoFormData({ ...infoFormData, name: e.target.value })}
              placeholder="Введите название сцены..."
              className="ssi-input"
              disabled={loading}
            />
          </div>

          <div className="ssi-form-group">
            <label>Теги:</label>
            <input
              type="text"
              value={infoFormData.tag}
              onChange={(e) => setInfoFormData({ ...infoFormData, tag: e.target.value })}
              placeholder="Введите теги сцены..."
              className="ssi-input"
              disabled={loading}
            />
            <small className="help-text">Слова через пробел</small>
          </div>

          <div className="ssi-form-group">
            <label>Сообщение сцены:</label>
            <textarea
              value={infoFormData.original_message}
              onChange={(e) => setInfoFormData({ ...infoFormData, original_message: e.target.value })}
              placeholder="Введите сообщение для этой статической сцены..."
              className="ssi-textarea"
              rows={4}
              disabled={loading}
            />
          </div>

          <div className="ssi-form-group">
            <label>Фон:</label>
            <CustomSelect
              value={infoFormData.background_id}
              onChange={(e) => setInfoFormData({ ...infoFormData, background_id: e.target.value })}
              className="app-select"
              disabled={loading}
              required
            >
              <option value="">Выберите фон</option>
              {novel?.assets?.backgrounds?.map((background) => (
                <option key={background.id} value={background.id}>
                  {background.name}
                </option>
              ))}
            </CustomSelect>
          </div>

          <div className="ssi-form-group">
            <label>Персонажи:</label>
            <div className="ssi-chars-list">
              {infoFormData.characters.map((character, index) => (
                <div key={index} className="ssi-char-item">
                  <div className="ssi-char-selectors">
                    <CustomSelect
                      value={character.character_id}
                      onChange={(e) => {
                        const updatedCharacters = [...infoFormData.characters];
                        updatedCharacters[index] = { ...updatedCharacters[index], character_id: e.target.value, sprite_mix_id: '', sprite_id: '' };
                        setInfoFormData({ ...infoFormData, characters: updatedCharacters });
                      }}
                      className="app-select"
                      disabled={loading}
                    >
                      <option value="">Выберите персонажа</option>
                      {novel?.characters?.map((char) => (
                        <option key={char.id} value={char.id}>
                          {char.prompt_name}
                        </option>
                      ))}
                    </CustomSelect>

                    <CustomSelect
                      value={character.sprite_mix_id}
                      onChange={(e) => {
                        const updatedCharacters = [...infoFormData.characters];
                        updatedCharacters[index] = { ...updatedCharacters[index], sprite_mix_id: e.target.value, sprite_id: '' };
                        setInfoFormData({ ...infoFormData, characters: updatedCharacters });
                      }}
                      className="app-select"
                      disabled={loading || !character.character_id}
                    >
                      <option value="">Выберите набор спрайтов</option>
                      {getSelectedCharacter(character.character_id)?.sprites?.map((spriteMix) => (
                        <option key={spriteMix.id} value={spriteMix.id}>
                          {spriteMix.name}
                        </option>
                      ))}
                    </CustomSelect>

                    <CustomSelect
                      value={character.sprite_id}
                      onChange={(e) => updateCharacter(index, 'sprite_id', e.target.value)}
                      className="app-select"
                      disabled={loading || !character.sprite_mix_id}
                    >
                      <option value="">Выберите спрайт</option>
                      {getSelectedSpriteMix(character.character_id, character.sprite_mix_id)?.sprite_ids?.map((spriteId) => (
                        <option key={spriteId} value={spriteId}>
                          {getSpriteName(spriteId)}
                        </option>
                      ))}
                    </CustomSelect>

                    <button
                      type="button"
                      onClick={() => removeCharacter(index)}
                      className="ssi-btn-delete"
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addCharacter}
                className="ssi-btn-cancel"
                disabled={loading}
              >
                Добавить персонажа
              </button>
            </div>
          </div>

          <div className="ssi-form-group">
            <label>Музыка:</label>
            <CustomSelect
              value={infoFormData.music_id}
              onChange={(e) => setInfoFormData({ ...infoFormData, music_id: e.target.value })}
              className="app-select"
              disabled={loading}
            >
              <option value="">Без музыки</option>
              {novel?.assets?.musics?.map((music) => (
                <option key={music.id} value={music.id}>
                  {music.name}
                </option>
              ))}
            </CustomSelect>
          </div>

          <div className="ssi-form-group">
            <label>Тип говорящего:</label>
            <CustomSelect
              value={infoFormData.speaker_type}
              onChange={(e) => setInfoFormData({
                ...infoFormData,
                speaker_type: e.target.value,
                speaker_id: e.target.value === 'user' ? null : (novel?.characters?.length > 0 ? novel.characters[0].id : '')
              })}
              className="app-select"
              disabled={loading}
            >
              <option value="character">Персонаж</option>
              <option value="user">Пользователь</option>
            </CustomSelect>
          </div>

          {infoFormData.speaker_type === 'character' && (
            <div className="ssi-form-group">
              <label>Говорящий:</label>
              <CustomSelect
                value={infoFormData.speaker_id || ''}
                onChange={(e) => setInfoFormData({ ...infoFormData, speaker_id: e.target.value })}
                className="app-select"
                disabled={loading}
                required
              >
                <option value="">Выберите персонажа-говорящего</option>
                {novel?.characters?.map((character) => (
                  <option key={character.id} value={character.id}>
                    {character.prompt_name}
                  </option>
                ))}
              </CustomSelect>
            </div>
          )}

          <div className="ssi-form-actions">
            <button
              type="button"
              onClick={handleSaveInfo}
              disabled={loading}
              className="ssi-btn-primary"
            >
              {loading ? 'Загрузка...' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={handleCancelInfo}
              disabled={loading}
              className="ssi-btn-cancel"
            >
              Отменить
            </button>
          </div>
        </div>
      ) : (
        <div className="ssi-display">
          <div className="ssi-item">
            <label>Название сцены:</label>
            <p>{scene?.name || 'Без названия'}</p>
          </div>

          <div className="ssi-item">
            <label>Теги:</label>
            <p>{scene?.tag || 'Без тегов'}</p>
          </div>

          <div className="ssi-item">
            <label>Сообщение сцены:</label>
            <p className="message-content">{scene?.original_message || 'Сообщение не задано'}</p>
          </div>

          <div className="ssi-item">
            <label>Фон:</label>
            <p>{getBackgroundName(scene?.background_id)}</p>
          </div>

          <div className="ssi-item">
            <label>Персонажи:</label>
            <div className="characters-display">
              {scene?.characters && scene.characters.length > 0 ? (
                scene.characters.map((character, index) => (
                  <div key={index} className="character-display-item">
                    <p><strong>{getCharacterName(character.character_id)}</strong></p>
                    <p>Набор спрайтов: {getSpriteMixName(character.character_id, character.sprite_mix_id)}</p>
                    <p>Спрайт: {getSpriteName(character.sprite_id)}</p>
                  </div>
                ))
              ) : (
                <p>Персонажи не настроены</p>
              )}
            </div>
          </div>

          <div className="ssi-item">
            <label>Музыка:</label>
            <p>{getMusicName(scene?.music_id)}</p>
          </div>

          <div className="ssi-item">
            <label>Тип говорящего:</label>
            <p>{scene?.speaker_type === 'user' ? 'Пользователь' : 'Персонаж'}</p>
          </div>

          {scene?.speaker_type === 'character' && (
            <div className="ssi-item">
              <label>Говорящий:</label>
              <p>{scene?.speaker_id ? getCharacterName(scene.speaker_id) : 'Не выбрано'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StaticSceneInfo;
